import { createClient, type SupabaseClient } from "@supabase/supabase-js";

/**
 * Arabise — Supabase client for the React app.
 *
 * Storage strategy mirrors the legacy site (js/supabase.js): localStorage
 * is the primary store, with the session ALSO written to a chunked cookie
 * (sb-<ref>-auth-token[.0,.1,…]) so the Cloudflare Pages worker can read
 * auth state server-side and gate page delivery by license_status.
 *
 * NEVER put the service-role key here. Only the PUBLISHABLE/anon key.
 */

export const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL      as string;
export const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error(
    "Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Did you create apps/main/.env?"
  );
}

const PROJECT_REF      = SUPABASE_URL.replace(/^https?:\/\//, "").split(".")[0];
const AUTH_COOKIE      = `sb-${PROJECT_REF}-auth-token`;
const STORAGE_KEY      = "arabise-auth";
const COOKIE_MAX_AGE   = 60 * 60 * 24 * 30;   // 30 days
const COOKIE_CHUNK_SIZE = 3200;                // ~4 KB browser cap, safe headroom
const COOKIE_MAX_CHUNKS = 20;

function writeAuthCookie(value: string) {
  if (typeof document === "undefined") return;
  clearAuthCookie();
  const encoded = encodeURIComponent(value);
  const secure  = (typeof location !== "undefined" && location.protocol === "https:") ? "; Secure" : "";
  const tail    = `; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;

  if (encoded.length <= COOKIE_CHUNK_SIZE) {
    document.cookie = `${AUTH_COOKIE}=${encoded}${tail}`;
    return;
  }
  let chunkIndex = 0;
  for (let pos = 0; pos < encoded.length; pos += COOKIE_CHUNK_SIZE) {
    const slice = encoded.slice(pos, pos + COOKIE_CHUNK_SIZE);
    document.cookie = `${AUTH_COOKIE}.${chunkIndex}=${slice}${tail}`;
    chunkIndex++;
  }
}

function clearAuthCookie() {
  if (typeof document === "undefined") return;
  document.cookie = `${AUTH_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  for (let i = 0; i < COOKIE_MAX_CHUNKS; i++) {
    document.cookie = `${AUTH_COOKIE}.${i}=; path=/; max-age=0; SameSite=Lax`;
  }
}

const cookieMirrorStorage = {
  getItem(key: string): string | null {
    if (typeof window === "undefined") return null;
    try { return window.localStorage.getItem(key); } catch { return null; }
  },
  setItem(key: string, value: string): void {
    if (typeof window === "undefined") return;
    try { window.localStorage.setItem(key, value); } catch { /* ignore */ }
    if (key === STORAGE_KEY) writeAuthCookie(value);
  },
  removeItem(key: string): void {
    if (typeof window === "undefined") return;
    try { window.localStorage.removeItem(key); } catch { /* ignore */ }
    if (key === STORAGE_KEY) clearAuthCookie();
  }
};

export const supabase: SupabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession:     true,
    autoRefreshToken:   true,
    detectSessionInUrl: true,
    flowType:           "implicit",
    storageKey:         STORAGE_KEY,
    storage:            cookieMirrorStorage
  }
});

// Belt-and-braces: keep the cookie in sync on every auth change in case
// some path bypasses the storage adapter (e.g. internal refresh write).
supabase.auth.onAuthStateChange((event, session) => {
  if (event === "SIGNED_OUT") {
    clearAuthCookie();
  } else if (session) {
    try {
      const value = window.localStorage.getItem(STORAGE_KEY);
      if (value) writeAuthCookie(value);
    } catch { /* ignore */ }
  }
});

// On first load, if a localStorage session exists from a previous visit
// before cookie mirroring, backfill the cookie now.
try {
  const existing = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
  if (existing) writeAuthCookie(existing);
} catch { /* ignore */ }
